// ./src/components/features/CreateEventWizard/steps/VouchersStep.tsx
//
// "Which vouchers will you give?" — shown when the event has the Vouchers feature on (ROADMAP A2b #62: a feature the
// host picks gets a step where they set up what it needs).
//
// ⚠⚠ WHY IT COMES BEFORE TICKETS (owner, 2026-09-29). Vouchers are GIVEN by rules that name a voucher type: a ticket
// type's grant ("2 drink vouchers per ticket") on the Tickets step, and an RSVP event's auto-grant ("1 per guest") here.
// A rule cannot name a voucher that does not exist yet, so the types are made first.
//
// Two parts:
//   1. THE VOUCHER TYPES — the account's catalog (a voucher is reusable across events), through the Library's own
//      `VoucherEditDrawer` (never a reduced form). The type's own ideas ("Free first drink") are one-tap starters.
//      ⚠ No remove here: a voucher type is ACCOUNT-wide and other events may be giving it.
//   2. HOW THEY ARE GIVEN — an RSVP event: the event's own `CouponGrantRulesDrawer` (auto-issued on reply). A ticketed
//      event: per ticket type, on the next step.
// ⚠ Four states for the list, in order: not read yet → skeleton; the read FAILED → the error (never "no vouchers");
//   none → the starters; else the rows. The read result is kept HERE, not taken from the context's `lastError`, which a
//   failed CREATE also sets — that would repaint a loaded list as unreadable.

import React, { useCallback, useEffect, useState } from 'react';
import { Stack, Group, Text, Button, Skeleton } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { CouponTypesProvider, InventoryItemsProvider, useCouponTypesContext, useOptionalEventSetupCatalogContext } from '@/contexts';
import { useEvent } from '@/hooks';
import { CouponType, WHOLE_SET } from '@/utils/api';
import { AppIcon, ErrorAlert } from '@/components/generic';
import { VoucherEditDrawer } from '@/components/features/Vouchers';
import { CouponGrantRulesDrawer } from '@/pages/private/EventCouponsPage/CouponGrantRulesDrawer';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { EVENT_MODE } from '../wizardTypes';

interface VoucherDrawerState {
  recordId: string | null;
  initialName?: string;
}

const VouchersStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const { draft, saving } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();
  const { couponTypes, list, isLoaded } = useCouponTypesContext();

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<VoucherDrawerState | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await list({}, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list]);

  useEffect(() => {
    void load();
  }, [load]);

  // The event, for its RSVP grant rules (settings.coupons) — read fresh, since the drawer merges into its settings.
  const { event, error: eventError, reload: reloadEvent } = useEvent(eventId);
  const isTicketed = draft.eventMode === EVENT_MODE.TICKETED;
  const grantRules = event?.settings?.coupons?.grantRules ?? [];
  const autoGrant = event?.settings?.coupons?.autoGrant !== false;

  const type = catalog?.findType(draft.eventCategoryId) ?? null;
  const ideas = (type?.offers?.find((o) => o.featureCode === 'vouchers')?.options?.voucherIdeas ?? []).filter(
    (idea) => !couponTypes.some((c) => (c.name ?? '').trim().toLowerCase() === idea.trim().toLowerCase()),
  );

  const nameOf = (couponTypeId: string): string =>
    couponTypes.find((c) => c.id === couponTypeId)?.name ?? t('events:wizard.vouchers.unknownVoucher', { defaultValue: 'a voucher' });

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const renderList = () => {
    if (!isLoaded && !readError) {
      return (
        <Stack gap="xs">
          <Skeleton height={56} radius="md" />
          <Skeleton height={56} radius="md" />
        </Stack>
      );
    }

    if (readError) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={readError}
            title={t('events:wizard.vouchers.loadFailed', { defaultValue: 'Could not load your vouchers' })}
            page="Create event — vouchers"
            operation="couponTypeList"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void load()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (couponTypes.length === 0) {
      return (
        <Text size="md" c="dimmed">
          {t('events:wizard.vouchers.none', {
            defaultValue: 'You have no vouchers yet. Create the ones this event will give — you can reuse them at later events.',
          })}
        </Text>
      );
    }

    return (
      <Stack gap="xs">
        {couponTypes.map((c: CouponType) => (
          <WizardItemRow
            key={c.id}
            icon={c.icon || 'Ticket'}
            iconColor={c.color}
            title={c.name ?? ''}
            subtitle={c.description}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: c.id as string })}
          />
        ))}
      </Stack>
    );
  };

  const renderRsvpRules = () => {
    if (eventError) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={eventError}
            title={t('events:wizard.vouchers.eventLoadFailed', { defaultValue: 'Could not load how this event gives vouchers' })}
            page="Create event — vouchers"
            operation="eventGet"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void reloadEvent()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (!event) {
      return <Skeleton height={48} radius="md" />;
    }

    return (
      <Stack gap="sm">
        {grantRules.length === 0 ? (
          <Text size="md" c="dimmed">
            {t('events:wizard.vouchers.noRules', { defaultValue: 'No vouchers are given yet — guests who reply get none.' })}
          </Text>
        ) : (
          <Stack gap={4}>
            {grantRules.map((rule, i) => (
              <Text key={`${rule.couponTypeId}-${i}`} size="md">
                {rule.grantPer === 'group'
                  ? t('events:wizard.vouchers.rulePerGroup', {
                      defaultValue: '{{quantity}} × {{name}} per party',
                      quantity: rule.quantity,
                      name: nameOf(rule.couponTypeId),
                    })
                  : t('events:wizard.vouchers.rulePerGuest', {
                      defaultValue: '{{quantity}} × {{name}} per guest',
                      quantity: rule.quantity,
                      name: nameOf(rule.couponTypeId),
                    })}
              </Text>
            ))}
            <Text size="md" c="dimmed">
              {autoGrant
                ? t('events:wizard.vouchers.autoOn', { defaultValue: 'Given automatically when a guest replies (or is approved).' })
                : t('events:wizard.vouchers.autoOff', { defaultValue: 'You give them from the event’s Vouchers page with “Generate from rules”.' })}
            </Text>
          </Stack>
        )}
        <Group>
          <Button
            variant="light"
            size="md"
            leftSection={<AppIcon iconName="RosetteDiscount" size={16} />}
            disabled={saving || couponTypes.length === 0}
            onClick={() => setRulesOpen(true)}
          >
            {grantRules.length > 0
              ? t('events:wizard.vouchers.editRules', { defaultValue: 'Change what guests get' })
              : t('events:wizard.vouchers.addRules', { defaultValue: 'Choose what guests get' })}
          </Button>
        </Group>
        {couponTypes.length === 0 && isLoaded ? (
          <Text size="md" c="dimmed">
            {t('events:wizard.vouchers.createFirst', { defaultValue: 'Create a voucher above first.' })}
          </Text>
        ) : null}
      </Stack>
    );
  };

  return (
    <Stack gap="xl">
      <Text size="md" c="dimmed">
        {t('events:wizard.vouchers.intro', {
          defaultValue:
            'Vouchers are things a guest redeems at the event — a free drink, a meal, a merch pickup. Create the ones this event will give, then choose who gets how many.',
        })}
      </Text>

      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="md">
            {t('events:wizard.vouchers.typesTitle', { defaultValue: 'Your vouchers' })}
          </Text>
          <Button
            variant="light"
            size="md"
            leftSection={<AppIcon iconName="Plus" size={16} />}
            disabled={saving}
            onClick={() => setDrawer({ recordId: null })}
          >
            {t('events:wizard.vouchers.add', { defaultValue: 'Create a voucher' })}
          </Button>
        </Group>
        {ideas.length > 0 && isLoaded && !readError ? (
          <Group gap="xs">
            {ideas.map((idea) => (
              <Button
                key={idea}
                variant="default"
                size="md"
                leftSection={<AppIcon iconName="Plus" size={16} />}
                disabled={saving}
                onClick={() => setDrawer({ recordId: null, initialName: idea })}
              >
                {idea}
              </Button>
            ))}
          </Group>
        ) : null}
        {renderList()}
      </Stack>

      <Stack gap="sm">
        <Text fw={600} size="md">
          {t('events:wizard.vouchers.givenTitle', { defaultValue: 'Who gets them' })}
        </Text>
        {isTicketed ? (
          <Text size="md" c="dimmed">
            {t('events:wizard.vouchers.perTicket', {
              defaultValue: 'On the next step, choose how many vouchers each ticket type gives its buyer.',
            })}
          </Text>
        ) : (
          renderRsvpRules()
        )}
      </Stack>

      <VoucherEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        initialName={drawer?.initialName ?? null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      {event && !isTicketed ? (
        <CouponGrantRulesDrawer
          rsvpFirst
          opened={rulesOpen}
          onClose={() => setRulesOpen(false)}
          event={event}
          onSaved={() => {
            setRulesOpen(false);
            void reloadEvent();
          }}
        />
      ) : null}
    </Stack>
  );
};

export const VouchersStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  // Scoped by the event, as on the event's Vouchers page: a co-host creating a voucher here carries the event's
  // auth context (`contextEventId`). The inventory provider feeds the voucher's item menu.
  return (
    <CouponTypesProvider pageSize={WHOLE_SET} eventId={createdEventId} autoLoad={false}>
      <InventoryItemsProvider pageSize={WHOLE_SET} eventId={createdEventId}>
        <VouchersStepBody eventId={createdEventId} />
      </InventoryItemsProvider>
    </CouponTypesProvider>
  );
};
